import { ListChecks, StickyNote } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { NoticeIcon } from 'common-src/components/base';

import { CLIENT_ROOT } from 'src/constants/paths';
import useTabs from 'src/hooks/useTabs';

import {
  addMinutesToClock,
  athenaUrl,
  formatClock12,
  getAppointmentTimezoneAbbreviation,
  getAppointmentTypeColor,
  isTaskReadinessComplete,
} from '../../helpers';
import BookingRail from '../BookingRail';
import HuddleNotes from '../HuddleNotes';
import HuddleTasksSection from '../HuddleTasksSection';
import {
  getNoticeDetail,
  getNoticeLabel,
  getNoticeTone,
  MocaDecision,
  MocaOutcomeIcon,
  useMocaPrompt,
} from '../MocaPrompt';
import ProviderAvatars from '../ProviderAvatars';
import styles from './HuddleVisitCard.module.scss';

const HuddleVisitCard = ({
  appt,
  tasks = [],
  markingTaskId,
  assigningTaskId,
  onMarkComplete,
  onAssignToMe,
  onOpenTask,
  showNotes = true,
  postVisit = false,
  notes,
  onPostNote,
  onEditNote,
}) => {
  const { onSelectTab } = useTabs();

  const [open, setOpen] = useState(true);
  const moca = useMocaPrompt(appt, 'Huddle');
  const openTasks = tasks.filter((task) => !isTaskReadinessComplete(task));
  const patient = appt?.patient;
  const dotColor = getAppointmentTypeColor(appt).dot;

  const localTime = appt?.time;
  const endTime = addMinutesToClock(localTime, appt?.durationMinutes);
  const timezoneAbbreviation = getAppointmentTimezoneAbbreviation(appt);
  const platformHref =
    patient?.id != null ? `${CLIENT_ROOT}/patients/details/${patient.id}/info` : null;
  const athenaHref = athenaUrl(
    patient?.athenaIntegration?.athenaPatientId,
    patient?.athenaIntegration?.practiceId,
  );

  return (
    <div className={styles.row}>
      <BookingRail
        time={localTime}
        durationMinutes={appt?.durationMinutes}
        appointmentTypeId={appt?.appointmentTypeId}
      />
      <div className={styles.card}>
        <div
          role="button"
          tabIndex={0}
          aria-expanded={open}
          className={styles.headerSection}
          onClick={() => setOpen((prev) => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setOpen((prev) => !prev);
            }
          }}
        >
          <div className={styles.headerRow}>
            <div className={styles.titleLine}>
              <span className={styles.dot} style={{ background: dotColor }} aria-hidden="true" />
              <span className={styles.visitType}>{appt?.visitType}</span>
              <span className={styles.timeSpan}>
                · {formatClock12(localTime)}–{formatClock12(endTime)}
                {timezoneAbbreviation ? ` (${timezoneAbbreviation})` : ''}
              </span>
            </div>
            <div
              className={styles.linkOuts}
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              {postVisit && <MocaOutcomeIcon appt={appt} onClick={() => setOpen(true)} />}
              {!postVisit && appt?.lowMocaPrompt && (
                <NoticeIcon
                  tone={getNoticeTone(moca.state)}
                  label={getNoticeLabel(moca.state)}
                  detail={getNoticeDetail(appt.latestMoca, moca.state)}
                  onClick={() => setOpen(true)}
                />
              )}
              {openTasks.length > 0 && (
                <NoticeIcon
                  tone="alert"
                  icon={ListChecks}
                  count={openTasks.length}
                  label={`${openTasks.length} outstanding task${openTasks.length === 1 ? '' : 's'}`}
                  detail={openTasks
                    .slice(0, 3)
                    .map((task) => task.title)
                    .filter(Boolean)
                    .join(' \u00b7 ')}
                  onClick={() => setOpen(true)}
                />
              )}
              {showNotes && notes?.length > 0 && (
                <NoticeIcon
                  tone="neutral"
                  icon={StickyNote}
                  count={notes.length}
                  label={`${notes.length} huddle note${notes.length === 1 ? '' : 's'}`}
                  onClick={() => setOpen(true)}
                />
              )}
              {platformHref && (
                <button
                  type="button"
                  className={styles.linkChip}
                  onClick={() => onSelectTab({ path: platformHref })}
                >
                  Platform
                </button>
              )}
              {athenaHref && (
                <a
                  href={athenaHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.linkChip}
                >
                  Athena
                </a>
              )}
            </div>
          </div>

          <div className={styles.metaLine}>
            <span className={styles.memberName}>{patient?.name}</span>
            {appt?.clinician && (
              <>
                <span className={styles.metaDivider}>·</span>
                <ProviderAvatars name={appt.clinician} />
              </>
            )}
            {appt?.caregiverNote && (
              <>
                <span className={styles.metaDivider}>·</span>
                <span>{appt.caregiverNote}</span>
              </>
            )}
          </div>

          {appt?.athenaScheduleNote && (
            <div className={styles.athenaNote}>
              <StickyNote size={13} className={styles.athenaNoteIcon} aria-hidden="true" />
              <span>
                <span className={styles.athenaNoteLabel}>Athena note ·</span>{' '}
                {appt.athenaScheduleNote}
              </span>
            </div>
          )}
        </div>

        {open && (
          <>
            {!postVisit && appt?.lowMocaPrompt && (
              <MocaDecision latestMoca={appt.latestMoca} moca={moca} />
            )}

            <HuddleTasksSection
              tasks={tasks}
              markingTaskId={markingTaskId}
              assigningTaskId={assigningTaskId}
              onMarkComplete={onMarkComplete}
              onAssignToMe={onAssignToMe}
              onOpenTask={onOpenTask}
            />

            {showNotes && (
              <HuddleNotes notes={notes} onPostNote={onPostNote} onEditNote={onEditNote} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

HuddleVisitCard.propTypes = {
  appt: PropTypes.shape({
    id: PropTypes.string,
    date: PropTypes.string,
    time: PropTypes.string,
    durationMinutes: PropTypes.number,
    visitType: PropTypes.string,
    appointmentTypeId: PropTypes.string,
    clinician: PropTypes.string,
    patient: PropTypes.object,
    caregiverNote: PropTypes.string,
    athenaScheduleNote: PropTypes.string,
    latestMoca: PropTypes.shape({ score: PropTypes.number, maxScore: PropTypes.number }),
    lowMocaPrompt: PropTypes.bool,
  }).isRequired,
  tasks: PropTypes.array,
  markingTaskId: PropTypes.string,
  assigningTaskId: PropTypes.object,
  onMarkComplete: PropTypes.func.isRequired,
  onAssignToMe: PropTypes.func.isRequired,
  onOpenTask: PropTypes.func.isRequired,
  showNotes: PropTypes.bool,
  postVisit: PropTypes.bool,
  notes: PropTypes.array,
  onPostNote: PropTypes.func,
  onEditNote: PropTypes.func,
};

export default HuddleVisitCard;
